import { useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { LocationBound } from '../../model/Location';

const USERS_ON_MAP = gql`
  query usersOnMap($minLat: Float!, $maxLat: Float!, $minLng: Float!, $maxLng: Float!) {
    usersOnMap(minLat: $minLat, maxLat: $maxLat, minLng: $minLng, maxLng: $maxLng) {
      id
      name
      username
      avatarUrl
      latitude
      longitude
    }
  }
`;

const EMPTY_USERS: never[] = [];

export const useGetUsersOnMap = (bounds: LocationBound) => {
  const skip =
    !bounds ||
    bounds.minLat == null ||
    bounds.maxLat == null ||
    bounds.minLng == null ||
    bounds.maxLng == null;
  const { data, previousData, loading } = useQuery(USERS_ON_MAP, {
    variables: {
      minLat: bounds?.minLat,
      maxLat: bounds?.maxLat,
      minLng: bounds?.minLng,
      maxLng: bounds?.maxLng
    },
    skip,
    fetchPolicy: 'cache-and-network'
  });
  // When the map pans, the bounds (query variables) change and Apollo clears
  // `data` to undefined until the new response lands. Returning [] during that
  // gap empties the pin layer and then refills it a moment later, which is what
  // makes the user pins blink on every pan. Falling back to previousData keeps
  // the existing pins on screen through the refetch; once the new result lands
  // (even if it is an empty set) the layer updates cleanly with no flicker.
  // EMPTY_USERS is a module-level constant so consumers' memo deps don't see
  // a fresh [] identity on every render while skipped/unanswered (plan A4).
  const rawUsers = data?.usersOnMap ?? previousData?.usersOnMap ?? EMPTY_USERS;
  // Null Island guard (seen live 2026-07-07: a "8" user cluster at 0,0):
  // opted-in users without real coordinates must not render as pins. Memoized
  // so the filtered array identity is stable for consumers' memo deps.
  // Hardened 2026-07-07 second pass: the truthy check let string "0" coords
  // through, so the Null Island cluster survived. Coerce to numbers and
  // reject non-finite or exactly-0,0 positions.
  const users = useMemo(
    () =>
      rawUsers.filter((u: any) => {
        const lat = Number(u?.latitude);
        const lng = Number(u?.longitude);
        return Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0);
      }),
    [rawUsers]
  );
  return {
    users,
    usersLoading: loading
  };
};
